// Command groups for the Triage cases step of "Decide which commands run
// without approval": what a case's cited tool call did, and the rules that
// ask about it or block it. A case falls in every group whose pattern matches
// one of its cited tool calls; a cited MCP tool is its own group. The list is
// fixed so the chat model cannot invent a group, and a group says in `note`
// which forms of the command its rules do not match.

const command = (target) => ({ kind: "command", target });
const powershell = (target) => ({ kind: "powershell", target });
const edit = (target) => ({ kind: "edit", target });

export const COMMAND_GROUPS = Object.freeze([
    {
        id: "send-data",
        name: "Data sent to another host",
        test: /(^|[\s;&|(])curl\s[^\n;&|]*(-X\s*POST|--request\s+POST|\s-d\s|--data|\s-F\s|--form|--upload-file|\s-T\s)/,
        rules: [command("curl * -X POST *"), command("curl * -d *"), command("curl * --data*"), command("curl * -F *"), command("curl * --upload-file *")],
        suggest: "ask",
        note: "These rules also match requests to local hosts. Data sent by email or by an MCP tool is not matched.",
    },
    {
        id: "copy-out",
        name: "Files copied to another machine",
        test: /(^|[\s;&|(])(scp|rsync)\s/,
        rules: [command("scp"), command("rsync")],
        suggest: "ask",
    },
    {
        id: "ssh-keys",
        name: "SSH authorized keys",
        test: /authorized_keys/,
        rules: [edit("~/.ssh/authorized_keys")],
        suggest: "deny",
        note: "The edit rule also covers shell redirects into the file.",
    },
    {
        id: "remote-shell",
        name: "Remote shell",
        test: /(^|[\s;&|(])ssh\s/,
        rules: [command("ssh")],
        suggest: "ask",
    },
    {
        id: "merge",
        name: "Pull request merges",
        test: /gh\s+pr\s+merge|gh\s+api[^\n;&|]*\/merge/,
        rules: [command("gh pr merge")],
        suggest: "deny",
        note: "A merge through gh api is not matched.",
    },
    {
        id: "pr-create",
        name: "Pull requests opened",
        test: /gh\s+pr\s+create/,
        rules: [command("gh pr create")],
        suggest: "ask",
    },
    {
        id: "push",
        name: "Pushes",
        test: /(^|[\s;&|(])git\s+push(\s|$)/,
        rules: [command("git push")],
        suggest: "ask",
        note: "git -C <folder> push is not matched.",
    },
    {
        id: "installers",
        name: "Downloaded scripts run in PowerShell",
        test: /invoke-expression|(^|[\s;|(])iex(\s|$)/i,
        rules: [powershell("Invoke-Expression"), powershell("iex")],
        suggest: "deny",
        note: "curl ... | sh cannot be one rule: agents split a pipeline before they check rules.",
    },
    {
        id: "approvals-off",
        name: "Agents started with approvals off",
        test: /--dangerously-skip-permissions|--yolo(\s|$)|--dangerously-bypass-approvals-and-sandbox|--allow-all-tools/,
        rules: [
            command("claude * --dangerously-skip-permissions*"), command("codex * --dangerously-bypass-approvals-and-sandbox*"),
            command("codex * --yolo*"), command("copilot * --allow-all-tools*"), command("copilot * --yolo*"),
        ],
        suggest: "deny",
    },
    {
        id: "secret-store",
        name: "Secret store reads",
        test: /secretsmanager\s+(list-secrets|get-secret-value)|gcloud\s+secrets\s+(list|versions\s+access)|keyvault\s+secret\s+(list|show)/i,
        rules: [command("aws secretsmanager list-secrets"), command("aws secretsmanager get-secret-value"), command("gcloud secrets"), command("az keyvault secret")],
        suggest: "ask",
    },
    {
        id: "grants",
        name: "Permission grants",
        test: /add-iam-policy-binding|set-iam-policy|aws\s+iam\s+(attach|put|create|add)|az\s+role\s+assignment\s+create/i,
        rules: [command("gcloud * add-iam-policy-binding *"), command("aws iam"), command("az role assignment")],
        suggest: "ask",
        note: "A command run through a shell variable, such as $g iam ..., is not matched.",
    },
]);

const MCP_PREFIX = "mcp__";

/// The command text and file path a cited tool call carries. Claude Code and
/// Copilot shells name it `command`, Codex `cmd`, and Codex code mode wraps
/// its commands in `input`.
export function callText(block) {
    const input = block?.input && typeof block.input === "object" ? block.input : {};
    const text = ["command", "cmd", "input"].map((k) => input[k]).filter((v) => typeof v === "string").join("\n");
    const path = ["file_path", "path", "notebook_path"].map((k) => input[k]).find((v) => typeof v === "string") ?? "";
    return { text, path };
}

/// The MCP tool a cited part names: the tool call's name, or the tool a cited
/// result came from.
const mcpToolOf = (block) => [block?.name, block?.tool_name, block?.linkedToolName].find((n) => typeof n === "string" && n.startsWith(MCP_PREFIX)) ?? null;

/// The group ids one cited tool call or tool result falls in.
export function groupsOfCall(block) {
    const out = [];
    const tool = mcpToolOf(block);
    if (tool) out.push(tool);
    if (block?.type !== "tool_use") return out;
    const { text, path } = callText(block);
    for (const group of COMMAND_GROUPS) if (group.test.test(text) || (path && group.test.test(path))) out.push(group.id);
    return out;
}

const TITLES = 3;

/// The rows of the Triage cases step from cases and their cited tool calls.
/// `cases` is `[{ id, title }]`, newest first; `calls` maps a case id to the
/// tool call and tool result blocks its citations point at. A group row
/// carries its rules and suggestion; the last row holds the cases no rule
/// covers.
export function caseGroupRows(cases, calls) {
    const byGroup = new Map();
    const uncovered = [];
    for (const c of cases) {
        const groups = new Set((calls.get(c.id) ?? []).flatMap(groupsOfCall));
        if (!groups.size) uncovered.push(c);
        for (const g of groups) byGroup.set(g, [...(byGroup.get(g) ?? []), c]);
    }
    const rows = [...byGroup].map(([id, list]) => {
        const group = COMMAND_GROUPS.find((g) => g.id === id);
        const base = group
            ? { name: group.name, rules: group.rules, suggest: group.suggest, note: group.note ?? "" }
            : { name: `MCP tool ${id}`, rules: [{ kind: "mcp", target: id }], suggest: "ask", note: "" };
        return { id, ...base, cases: list.length, titles: list.slice(0, TITLES).map((c) => c.title), total: cases.length };
    }).sort((a, b) => b.cases - a.cases || a.name.localeCompare(b.name));
    if (uncovered.length) {
        rows.push({ id: "none", name: "No command rule applies", noRule: true, rules: [], cases: uncovered.length, titles: uncovered.slice(0, TITLES).map((c) => c.title), total: cases.length });
    }
    return rows;
}
